/**
 * Accept Invite Page
 * 
 * Token-based invite acceptance flow:
 * 1. Validate token
 * 2. Show org name and email
 * 3. Set name and password
 * 4. Auto-login and redirect to Command Centre
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle, Loader } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  
  const [status, setStatus] = useState('validating'); // validating, valid, invalid, accepting, success, error
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  
  // Form state
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Password validation state
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API}/api/invites/validate/${token}`);
        if (response.data.valid) {
          setInviteData(response.data);
          setStatus('valid');
        } else {
          setError(response.data.message || 'Invalid invite');
          setStatus('invalid');
        }
      } catch (err) {
        setError('Failed to validate invite');
        setStatus('invalid');
      }
    };
    
    validateToken();
  }, [token]);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('At least one lowercase letter');
    if (!/\d/.test(pwd)) errors.push('At least one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('At least one special character');
    return errors;
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordErrors(validatePassword(pwd));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setError('Password does not meet requirements');
      return;
    }
    
    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    
    setStatus('accepting');
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/invites/accept`, {
        token,
        name: name.trim(),
        password
      });
      
      if (response.data.success) {
        setStatus('success');
        
        // Auto-login using the returned tokens
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        
        // Wait briefly then redirect
        setTimeout(() => {
          window.location.href = '/command-centre';
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invite');
      setStatus('error');
    }
  };

  // Loading state
  if (status === 'validating') {
    return (
      <div style={styles.container} data-testid="accept-invite-page">
        <div style={styles.card}>
          <Loader size={40} style={{ ...styles.icon, animation: 'spin 1s linear infinite' }} />
          <h2 style={styles.title}>Validating Invite</h2>
          <p style={styles.subtitle}>Please wait...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (status === 'invalid') {
    return (
      <div style={styles.container} data-testid="accept-invite-page">
        <div style={styles.card}>
          <XCircle size={48} style={{ ...styles.icon, color: '#ef4444' }} />
          <h2 style={styles.title}>Invalid Invite</h2>
          <p style={styles.subtitle}>{error}</p>
          <button
            onClick={() => navigate('/login')}
            style={styles.primaryBtn}
            data-testid="go-to-login-btn"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div style={styles.container} data-testid="accept-invite-page">
        <div style={styles.card}>
          <CheckCircle size={48} style={{ ...styles.icon, color: '#22c55e' }} />
          <h2 style={styles.title}>Welcome to TradeLife!</h2>
          <p style={styles.subtitle}>Your account has been created. Redirecting...</p>
        </div>
      </div>
    );
  }

  // Valid token - show form
  return (
    <div style={styles.container} data-testid="accept-invite-page">
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <Shield size={32} style={{ color: '#3b82f6' }} />
          <div>
            <span style={styles.logoTrade}>Trade</span>
            <span style={styles.logoLife}>Life</span>
          </div>
        </div>

        <div style={styles.inviteInfo}>
          <p style={styles.inviteText}>
            You&apos;ve been invited to join <strong>{inviteData.org_name}</strong>
          </p>
          <div style={{
            ...styles.roleChip,
            background: inviteData.role === 'MANAGER' 
              ? 'rgba(59, 130, 246, 0.1)' 
              : 'rgba(34, 197, 94, 0.1)',
            borderColor: inviteData.role === 'MANAGER' 
              ? 'rgba(59, 130, 246, 0.3)' 
              : 'rgba(34, 197, 94, 0.3)'
          }}>
            <span style={styles.roleLabel}>You&apos;re joining as: </span>
            <span style={{
              ...styles.roleValue,
              color: inviteData.role === 'MANAGER' ? '#3b82f6' : '#22c55e'
            }}>
              {inviteData.role}
            </span>
          </div>
          
          {/* Role description */}
          <div style={{
            marginTop: '12px',
            padding: '10px 14px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '8px',
            fontSize: '0.8125rem',
            color: '#94a3b8',
            lineHeight: '1.5'
          }}>
            {inviteData.role === 'MANAGER' ? (
              <>Full access to jobs, scheduling, and materials. Finance access may be granted separately.</>
            ) : (
              <>View and update assigned tasks, access scheduling and view materials. Restricted access to sensitive data.</>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={inviteData.email}
              disabled
              style={{ ...styles.input, opacity: 0.7 }}
              data-testid="email-input"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              style={styles.input}
              data-testid="name-input"
              required
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                placeholder="Create a strong password"
                style={styles.input}
                data-testid="password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password && passwordErrors.length > 0 && (
              <ul style={styles.passwordRequirements}>
                {passwordErrors.map((err, i) => (
                  <li key={i} style={styles.requirementItem}>
                    <XCircle size={12} style={{ color: '#ef4444' }} />
                    {err}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              style={styles.input}
              data-testid="confirm-password-input"
              required
            />
            {confirmPassword && password !== confirmPassword && (
              <span style={styles.errorText}>Passwords do not match</span>
            )}
          </div>

          {error && status === 'error' && (
            <div style={styles.errorBox}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'accepting' || passwordErrors.length > 0 || password !== confirmPassword}
            style={{
              ...styles.primaryBtn,
              opacity: (status === 'accepting' || passwordErrors.length > 0 || password !== confirmPassword) ? 0.6 : 1
            }}
            data-testid="accept-invite-btn"
          >
            {status === 'accepting' ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Creating Account...
              </>
            ) : (
              'Create Account & Join'
            )}
          </button>
        </form>

        <p style={styles.footer}>
          Already have an account? <a href="/login" style={styles.link}>Sign in</a>
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    padding: '24px'
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: '#1e222d',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '32px',
    justifyContent: 'center'
  },
  logoTrade: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#f1f5f9'
  },
  logoLife: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#3b82f6'
  },
  icon: {
    display: 'block',
    margin: '0 auto 16px',
    color: '#3b82f6'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#f1f5f9',
    textAlign: 'center',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '0.9375rem',
    color: '#94a3b8',
    textAlign: 'center',
    marginBottom: '24px'
  },
  inviteInfo: {
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    textAlign: 'center'
  },
  inviteText: {
    fontSize: '0.9375rem',
    color: '#94a3b8',
    margin: '0 0 12px'
  },
  roleChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(59, 130, 246, 0.2)',
    padding: '6px 12px',
    borderRadius: '20px'
  },
  roleLabel: {
    fontSize: '0.8125rem',
    color: '#64748b'
  },
  roleValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#3b82f6'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#94a3b8'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '0.9375rem',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: '#f1f5f9',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  },
  passwordWrapper: {
    position: 'relative'
  },
  eyeBtn: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px'
  },
  passwordRequirements: {
    margin: '8px 0 0',
    padding: '0',
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.75rem',
    color: '#ef4444'
  },
  errorText: {
    fontSize: '0.75rem',
    color: '#ef4444',
    marginTop: '4px'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '10px',
    fontSize: '0.875rem',
    color: '#ef4444'
  },
  primaryBtn: {
    width: '100%',
    padding: '14px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: 'white',
    background: '#3b82f6',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'opacity 0.2s'
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#64748b'
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'none'
  }
};

export default AcceptInvite;
